import { useState } from 'react';
import { generatePath, Link } from 'react-router';

import { AppRoutes } from '@/constants';
import { formatDate } from '@/utils';

import { EyeIcon, EyeOffIcon } from '@/assets/icons';
import { Badge, Button, SuspenseImage, SvgIcon } from '@/components/common';

import { ProfileType } from '@/features/check-in-logs/enums';
import { ICheckInLogDetailsCard } from '@/features/check-in-logs/interfaces';
import {
  getCheckInStatusIcon,
  getCheckInStatusLabel,
  getCheckInStatusVariant,
  getProfileTypeLabel,
} from '@/features/check-in-logs/utils';

const CheckInLogDetailsCard = ({ checkInLogDetails }: ICheckInLogDetailsCard) => {
  const [isCheckInPhotoHidden, setCheckInPhotoHiddenValue] = useState(true);
  return (
    <div className="rounded-softer border border-neutral-n4 bg-neutral-white">
      <div className="text-sm flex flex-col gap-y-base-plus p-m">
        {/* Timestamp */}
        <div className="flex gap-x-xs">
          <div className="font-semibold text-brand-primary-darkest w-[120px] sm:w-[250px]">
            Timestamp:
          </div>
          <div className="font-medium text-neutral-n8 flex-1 break-all">
            {formatDate({
              date: new Date(checkInLogDetails.timestamp),
              hasTime: true,
            })}
          </div>
        </div>

        {/* Face Profile ID */}
        <div className="flex gap-x-xs">
          <div className="font-semibold text-brand-primary-darkest w-[120px] sm:w-[250px]">
            Face Profile ID:
          </div>
          <div className="font-medium flex-1 break-all">
            {checkInLogDetails.profileId ? (
              <Link
                className="text-brand-primary-base underline"
                to={generatePath(AppRoutes.FACE_ID_PROFILE_DETAILS, {
                  id: checkInLogDetails.profileId,
                })}
                target="_blank"
                rel="noopener noreferrer"
              >
                {checkInLogDetails.profileId}
              </Link>
            ) : (
              <span className="tracking-[1px]">--</span>
            )}
          </div>
        </div>

        {/* Full Name */}
        <div className="flex gap-x-xs">
          <div className="font-semibold text-brand-primary-darkest w-[120px] sm:w-[250px]">
            Full Name:
          </div>
          <div className="font-medium text-neutral-n8 flex-1 break-all">
            {checkInLogDetails.fullName || <span className="tracking-[1px]">--</span>}
          </div>
        </div>

        {/* Profile Type */}
        <div className="flex gap-x-xs">
          <div className="font-semibold text-brand-primary-darkest w-[120px] sm:w-[250px]">
            Profile Type:
          </div>
          <div className="font-medium text-neutral-n8 flex-1 break-all">
            {checkInLogDetails.profileId ? (
              getProfileTypeLabel(checkInLogDetails.profileType)
            ) : (
              <span className="tracking-[1px]">--</span>
            )}
          </div>
        </div>

        {/* Status */}
        <div className="flex gap-x-xs items-center">
          <div className="font-semibold text-brand-primary-darkest w-[120px] sm:w-[250px]">
            Check-in Status:
          </div>
          <Badge variant={getCheckInStatusVariant(checkInLogDetails.status)}>
            <span className="rounded-full">
              <SvgIcon
                className="size-base"
                component={getCheckInStatusIcon(checkInLogDetails.status)}
              />
            </span>
            {getCheckInStatusLabel(checkInLogDetails.status)}
          </Badge>
        </div>

        {checkInLogDetails.profileType === ProfileType.Employee && (
          <>
            {/* Employee ID */}
            <div className="flex gap-x-xs">
              <div className="font-semibold text-brand-primary-darkest w-[120px] sm:w-[250px]">
                Employee ID:
              </div>
              <div className="font-medium text-neutral-n8 flex-1 break-all">
                {checkInLogDetails.employeeId || <span className="tracking-[1px]">--</span>}
              </div>
            </div>
          </>
        )}

        {/* Room */}
        <div className="flex gap-x-xs">
          <div className="font-semibold text-brand-primary-darkest w-[120px] sm:w-[250px]">
            Room:
          </div>
          <div className="font-medium text-neutral-n8 flex-1 break-all">
            {checkInLogDetails.device ? (
              checkInLogDetails.device.name
            ) : (
              <span className="tracking-[1px]">--</span>
            )}
          </div>
        </div>

        {/* View Check-in Photo Button */}
        <div className="flex gap-x-xs items-center">
          <div className="font-semibold text-brand-primary-darkest w-[120px] sm:w-[250px]">
            Check-in Photo:
          </div>
          <div className="flex-1">
            {isCheckInPhotoHidden ? (
              <Button
                variant="secondary"
                borderRadius="soft"
                prefixIcon={<EyeIcon />}
                onClick={() => setCheckInPhotoHiddenValue(false)}
              >
                View Check-in Photo
              </Button>
            ) : (
              <Button
                variant="secondary"
                borderRadius="soft"
                prefixIcon={<EyeOffIcon />}
                onClick={() => setCheckInPhotoHiddenValue(true)}
              >
                Hide Check-in Photo
              </Button>
            )}
          </div>
        </div>

        {/* Check-in Photo */}
        {!isCheckInPhotoHidden && (
          <div className="flex gap-x-xs">
            <div className="font-semibold text-brand-primary-darkest w-[120px] sm:w-[250px]"></div>
            <SuspenseImage
              src={checkInLogDetails.photoUrl}
              alt="Check-in"
              className="h-40 w-[230px] object-cover"
            />
          </div>
        )}

        {/* Service Logs */}
        <div className="flex gap-x-xs items-center min-h-base-plus">
          <div className="font-semibold text-brand-primary-darkest w-[120px] sm:w-[250px]">
            Service Logs:
          </div>
          <div className="font-medium text-neutral-n8 flex-1 break-all">
            {checkInLogDetails.serviceLogs || <span className="tracking-[1px]">--</span>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckInLogDetailsCard;
